<script setup lang="ts">
/**
 * The gallery in the middle of /templates: the CV layouts and the web-card
 * layouts as framed previews in the template's card grid. Each card links to a
 * live public example and has a button that opens a blank CV on that layout.
 *
 * It sits between the format explanation and the rest of the guide, so it no
 * longer carries a closing call to action of its own — MarketingTemplatesGuide
 * and SofaxPageCta do that. `detailKey` is the one line that tells a visitor
 * what actually differs between the layouts, which is the question the previews
 * alone never answer.
 */
import type { StartCVFormOptions } from '~/composables/useStartCVForm'
import { PDF_TEMPLATE_META, TEMPLATE_TAGS, isPdfTemplate, type TemplateTag } from '~/constants/cvTemplates'

const { t } = useI18n()
const { startCVForm } = useStartCVForm()

interface TemplateData {
  id: string
  /** What /cv/new should preselect when the visitor starts from this card */
  start: StartCVFormOptions
  nameKey: string
  descriptionKey: string
  /** One concrete line on how this layout is built, not how it feels. */
  detailKey: string
  image: string
  type: 'cv' | 'web'
  /**
   * The example's short public URL (its public_url_slug), not /cv/public/<uuid>,
   * which only redirects there (utils/publicCvLink.ts). If a sample CV ever gets
   * a new slug, update its link here.
   */
  previewUrl?: string
}

const cvTemplatesData: TemplateData[] = [
  {
    id: 'ats',
    start: { template: 'ats' },
    nameKey: 'cv.templates.ats.name',
    descriptionKey: 'cv.templates.ats.description',
    detailKey: 'templatesPage.gallery.details.ats',
    image: '/ats.png',
    type: 'cv',
    previewUrl: 'https://www.inmyresume.com/cv/alexandra-johnson-5',
  },
  {
    id: 'basic',
    start: { template: 'basic' },
    nameKey: 'cv.templates.basic.name',
    descriptionKey: 'cv.templates.basic.description',
    detailKey: 'templatesPage.gallery.details.basic',
    image: '/basic.png',
    type: 'cv',
    previewUrl: 'https://www.inmyresume.com/cv/alexandra-johnson-4',
  },
  {
    id: 'classic',
    start: { template: 'classic' },
    nameKey: 'cv.templates.classic.name',
    descriptionKey: 'cv.templates.classic.description',
    detailKey: 'templatesPage.gallery.details.classic',
    image: '/classic.png',
    type: 'cv',
    previewUrl: 'https://www.inmyresume.com/cv/alexandra-johnson-3',
  },
  {
    id: 'modern',
    start: { template: 'modern' },
    nameKey: 'cv.templates.modern.name',
    descriptionKey: 'cv.templates.modern.description',
    detailKey: 'templatesPage.gallery.details.modern',
    image: '/modern.png',
    type: 'cv',
    previewUrl: 'https://www.inmyresume.com/cv/alexandr-johnson',
  },
  {
    id: 'minimal',
    start: { template: 'minimal' },
    nameKey: 'cv.templates.minimal.name',
    descriptionKey: 'cv.templates.minimal.description',
    detailKey: 'templatesPage.gallery.details.minimal',
    image: '/minimal.png',
    type: 'cv',
    previewUrl: 'https://www.inmyresume.com/cv/alexandra-johnson-2',
  },
  {
    id: 'executive',
    start: { template: 'executive' },
    nameKey: 'cv.templates.executive.name',
    descriptionKey: 'cv.templates.executive.description',
    detailKey: 'templatesPage.gallery.details.executive',
    image: '/executive.png',
    type: 'cv',
  },
  {
    id: 'europass',
    start: { template: 'europass' },
    nameKey: 'cv.templates.europass.name',
    descriptionKey: 'cv.templates.europass.description',
    detailKey: 'templatesPage.gallery.details.europass',
    image: '/europass.png',
    type: 'cv',
  },
  {
    id: 'creative',
    start: { template: 'creative' },
    nameKey: 'cv.templates.creative.name',
    descriptionKey: 'cv.templates.creative.description',
    detailKey: 'templatesPage.gallery.details.creative',
    image: '/creative.png',
    type: 'cv',
  },
]

/* Filter chips over the CV gallery, from each template's tags. */
const activeTag = ref<TemplateTag | null>(null)
const visibleCvTemplates = computed(() => cvTemplatesData.filter(item =>
  !activeTag.value || (isPdfTemplate(item.id) && PDF_TEMPLATE_META[item.id].tags.includes(activeTag.value))))

// Signature leads the row: it is the default every new web CV is created with.
const webTemplatesData: TemplateData[] = [
  {
    id: 'signature-web',
    start: { webTemplate: 'signature' },
    nameKey: 'cv.businessWebTemplates.signature.name',
    descriptionKey: 'cv.businessWebTemplates.signature.description',
    detailKey: 'templatesPage.gallery.details.signatureWeb',
    image: '/web-signature.png',
    type: 'web',
  },
  {
    id: 'sofax-web',
    start: { webTemplate: 'sofax' },
    nameKey: 'cv.businessWebTemplates.sofax.name',
    descriptionKey: 'cv.businessWebTemplates.sofax.description',
    detailKey: 'templatesPage.gallery.details.sofaxWeb',
    image: '/web-sofax.png',
    type: 'web',
    previewUrl: 'https://www.inmyresume.com/web/alexandra-johnson-5',
  },
  {
    id: 'classic-web',
    start: { webTemplate: 'classic' },
    nameKey: 'cv.businessWebTemplates.classic.name',
    descriptionKey: 'cv.businessWebTemplates.classic.description',
    detailKey: 'templatesPage.gallery.details.classicWeb',
    image: '/web-classic.png',
    type: 'web',
    previewUrl: 'https://www.inmyresume.com/web/alexandra-johnson-3',
  },
  {
    id: 'modern-web',
    start: { webTemplate: 'modern' },
    nameKey: 'cv.businessWebTemplates.modern.name',
    descriptionKey: 'cv.businessWebTemplates.modern.description',
    detailKey: 'templatesPage.gallery.details.modernWeb',
    image: '/web-modern.png',
    type: 'web',
    previewUrl: 'https://www.inmyresume.com/web/alexandra-johnson-2',
  },
]

/** Both galleries share one card; only the image crop differs. */
const galleries = computed(() => [
  { id: 'cv', anchor: 'cv-templates', titleKey: 'templatesPage.gallery.cvTitle', subtitleKey: 'templatesPage.gallery.cvSubtitle', items: visibleCvTemplates.value, cols: 'md:grid-cols-2 lg:grid-cols-3' },
  { id: 'web', anchor: 'web-templates', titleKey: 'templatesPage.gallery.webTitle', subtitleKey: 'templatesPage.gallery.webSubtitle', items: webTemplatesData, cols: 'md:grid-cols-2' },
])
</script>

<template>
  <section id="templates" class="sofax surface-alt relative overflow-hidden">
    <div class="section-space">
      <div class="shell relative z-10">
        <SofaxSectionHeading
          :eyebrow="t('templatesPage.gallery.eyebrow')"
          :title="t('templatesPage.gallery.title')"
          :text="t('templatesPage.gallery.subtitle')"
          width="max-w-4xl"
        />

        <div class="grid grid-cols-1 gap-y-20 lg:gap-y-[6.25rem]">
          <!-- Both formats from one CV: the template's dark content block -->
          <div class="mx-auto flex w-full max-w-3xl items-start gap-5 rounded-[0.625rem] bg-ink p-[1.875rem] text-white dark:bg-gray-800">
            <SofaxIconTile icon="bi-lightning-charge-fill" round size="sm" />
            <div class="flex-1">
              <div class="display-heading display-heading-4 mb-3 !text-white">{{ t('homepage.templates.bothFormats') }}</div>
              <p class="text-base !text-white/75">{{ t('homepage.templates.bothFormatsDescription') }}</p>
            </div>
          </div>

          <!-- Galleries -->
          <div v-for="gallery in galleries" :id="gallery.anchor" :key="gallery.id">
            <div class="mb-10 flex flex-wrap items-end justify-between gap-6 lg:mb-14">
              <div class="max-w-2xl">
                <h3>{{ t(gallery.titleKey) }}</h3>
                <p class="mt-3">{{ t(gallery.subtitleKey) }}</p>
                <p v-if="gallery.id === 'cv'" class="mt-3 text-base text-muted dark:text-gray-400">{{ t('templatesPage.gallery.customise') }}</p>
              </div>
            </div>

            <!-- Filters (CV layouts only) -->
            <div v-if="gallery.id === 'cv'" class="-mt-4 mb-10 flex flex-wrap gap-2 lg:-mt-6" role="group" :aria-label="t('templatesPage.gallery.filterLabel')">
              <button
                v-for="tag in [null, ...TEMPLATE_TAGS]"
                :key="tag ?? 'all'"
                type="button"
                class="rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors"
                :class="activeTag === tag
                  ? 'border-ink bg-ink text-white dark:border-white dark:bg-white dark:text-ink'
                  : 'border-line bg-white text-ink hover:border-ink/40 dark:border-gray-700 dark:bg-gray-800 dark:text-white'"
                :aria-pressed="activeTag === tag"
                @click="activeTag = tag">
                {{ t(`templatesPage.gallery.filters.${tag ?? 'all'}`) }}
              </button>
            </div>

            <ul class="grid grid-cols-1 gap-x-6 gap-y-10" :class="gallery.cols">
              <li v-for="template in gallery.items" :key="template.id">
                <!-- The card is no longer one big link: preview and create are two actions -->
                <div class="group flex h-full flex-col rounded-[0.625rem] bg-white p-[1.875rem] transition-all duration-300 hover:shadow-lift dark:bg-gray-800">
                  <a :href="template.previewUrl" target="_blank" rel="noopener noreferrer" class="block">
                  <!-- Framed preview with the template's offset shadow -->
                  <div class="img-shadow mb-8">
                    <div class="relative overflow-hidden rounded-[0.625rem] border border-ink bg-surface dark:border-gray-600 dark:bg-gray-700" :class="template.type === 'web' ? 'aspect-[4/3]' : 'aspect-[1/1.4]'">
                      <NuxtImg
                        :src="template.image"
                        :alt="`${t(template.nameKey)} preview`"
                        format="webp"
                        quality="85"
                        loading="lazy"
                        class="size-full transition-transform duration-300 group-hover:scale-105"
                        :class="template.type === 'web' ? 'object-cover object-top' : 'object-contain object-top'"
                      />
                      <div class="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-ink/60 text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                        <i class="bi bi-eye text-3xl" />
                        <span class="text-sm font-semibold">{{ t('common.preview') }}</span>
                      </div>
                    </div>
                  </div>

                  </a>

                  <div class="display-heading display-heading-4 mb-3">{{ t(template.nameKey) }}</div>
                  <p class="!mb-4 text-base">{{ t(template.descriptionKey) }}</p>

                  <!-- The line that actually separates the layouts from each other -->
                  <p class="!mb-6 border-l-2 border-line pl-4 text-base text-muted dark:border-gray-700 dark:text-gray-400">
                    {{ t(template.detailKey) }}
                  </p>

                  <div class="mt-auto flex flex-wrap items-center justify-between gap-4">
                    <SofaxBtn :label="t('homepage.templates.useTemplate')" variant="accent" type="button" @click="startCVForm(template.start)" />
                    <SofaxArrowLink v-if="template.previewUrl" :to="template.previewUrl" target="_blank" rel="noopener noreferrer" :label="t('homepage.templates.previewOnline')" />
                  </div>
                </div>
              </li>
            </ul>
          </div>

          <!--
            Slimmed on purpose: the page has a call to action under every band
            and its own closing CTA, so this one only offers the thing the
            gallery makes you want, which is a filled layout to look at.
          -->
          <div class="mx-auto max-w-3xl text-center">
            <div class="display-heading display-heading-3 mb-4">{{ t('templatesPage.gallery.demoTitle') }}</div>
            <p>{{ t('templatesPage.gallery.demoText') }}</p>
            <div class="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <SofaxBtn to="/demo" :label="t('templatesPage.gallery.demoCta')" variant="dark" />
              <SofaxArrowLink to="/free-cv-builder" :label="t('templatesPage.gallery.builderCta')" />
            </div>
          </div>
        </div>
      </div>

      <img
        src="/sofax/background-shape-2.svg"
        alt=""
        aria-hidden="true"
        width="174"
        height="171"
        class="pointer-events-none absolute right-28 top-16 -z-10 hidden max-w-[153px] opacity-70 xl:inline-block"
      >
    </div>
  </section>
</template>
